import * as React from 'react';
import {formatShare} from 'src/AnalyticsFlowView/flowModel';
import type {FlowNode, TieredFlowModel} from 'src/AnalyticsFlowView/flowModel';

type Props = {
  model: TieredFlowModel;
  /**
   * Invoked for nodes that carry a key: income sources, the Taxes tier
   * when a taxes category is bound, and the spend categories.
   */
  onSelectCategory?: (key: string) => void;
  formatValue: (cents: number) => string;
};

const CHART_H = 400;
const VIEW_W = 1100;
const NODE_W = 10;
const GAP = 7;
const TOP = 24;
const X_LEFT = 225;
const X_SPINE = 430;
const X_MID = 520;
const X_GROUP = 700;
const X_RIGHT = 880;
const MIN_NODE_H = 6;

// Fixed color slots for the semantic roles; spend categories cycle the
// CVD-validated palette in slot order (see globals.css).
const KIND_COLOR: Record<string, string> = {
  income: 'var(--chart-3)',
  'reserve-draw': 'var(--muted-foreground)',
  withheld: 'var(--chart-4)',
  'taxes-tier': 'var(--chart-1)',
  'spending-tier': 'var(--muted-foreground)',
  'saving-tier': 'var(--chart-3)',
  unreviewed: 'var(--muted-foreground)',
};

// The unreviewed band is a to-do, not a flow: it draws hollow with a
// dashed outline so it reads as provisional next to the solid bars.
const nodeRectStyle = (laid: Laid): React.SVGProps<SVGRectElement> =>
  laid.node.kind === 'unreviewed'
    ? {
        fillOpacity: 0.25,
        stroke: laid.color,
        strokeWidth: 1,
        strokeDasharray: '3 2',
      }
    : {};

// A group's user-chosen color (category color list) wins; otherwise spend
// nodes and group aggregates cycle the palette in column order.
const nodeColor = (node: FlowNode, spendIndex: number): string => {
  if (node.kind === 'spend' || node.kind === 'spend-group') {
    return node.color != null
      ? `var(--color-${node.color}-500)`
      : `var(--chart-${(spendIndex % 8) + 1})`;
  }
  return KIND_COLOR[node.kind] ?? 'var(--muted-foreground)';
};

const spineHeading = (model: TieredFlowModel): string => {
  const extras = [
    ...(model.totals.fromSavings > 0 ? ['savings'] : []),
    ...(model.totals.unreviewed > 0 ? ['unreviewed'] : []),
  ];
  return extras.length === 0
    ? 'All income'
    : `Income + ${extras.join(' + ')}`;
};

const drawnValueOf = (node: FlowNode): number =>
  node.displayValue ?? node.value;

type Laid = {
  node: FlowNode;
  y: number;
  h: number;
  /** Contiguous position on the spine (no gaps), for the ribbon ends. */
  spineY: number;
  drawnValue: number;
  color: string;
};

const layout = (nodes: FlowNode[], scale: number): Laid[] => {
  let y = TOP;
  let spineY = TOP;
  let spendIndex = 0;
  return nodes.map((node) => {
    const drawnValue = drawnValueOf(node);
    const h = Math.max(MIN_NODE_H, drawnValue * scale);
    const laid: Laid = {
      node,
      y,
      h,
      spineY,
      drawnValue,
      color: nodeColor(
        node,
        node.kind === 'spend' || node.kind === 'spend-group' ? spendIndex : 0,
      ),
    };
    if (node.kind === 'spend' || node.kind === 'spend-group') {
      spendIndex += 1;
    }
    y += h + GAP;
    spineY += drawnValue * scale;
    return laid;
  });
};

const ribbonPath = (
  x0: number,
  y0: number,
  h0: number,
  x1: number,
  y1: number,
  h1: number,
): string => {
  const mid = (x0 + x1) / 2;
  return [
    `M ${x0} ${y0}`,
    `C ${mid} ${y0}, ${mid} ${y1}, ${x1} ${y1}`,
    `L ${x1} ${y1 + h1}`,
    `C ${mid} ${y1 + h1}, ${mid} ${y0 + h0}, ${x0} ${y0 + h0}`,
    'Z',
  ].join(' ');
};

type BreakGlyphProps = {x0: number; x1: number; y: number};

// The axis-break convention: a background-colored gap crossed by two
// slanted hairlines, laid over the node bar and the mouth of its ribbon.
const BreakGlyph = ({x0, x1, y}: BreakGlyphProps) => (
  <g>
    <line
      x1={x0}
      y1={y + 4}
      x2={x1}
      y2={y - 4}
      className="stroke-background"
      strokeWidth={9}
    />
    <line
      x1={x0}
      y1={y + 8}
      x2={x1}
      y2={y}
      className="stroke-muted-foreground"
      strokeWidth={1.5}
    />
    <line
      x1={x0}
      y1={y}
      x2={x1}
      y2={y - 8}
      className="stroke-muted-foreground"
      strokeWidth={1.5}
    />
  </g>
);

const truncate = (label: string, max = 24): string =>
  label.length > max ? `${label.slice(0, max - 1)}…` : label;

type NodeLabelProps = {
  laid: Laid;
  x: number;
  anchor: 'start' | 'end';
  formatValue: (cents: number) => string;
  /** The node's share of its column ("34%"), when meaningful. */
  share?: string | null;
  /**
   * Wrap the sublabel onto its own line from 30px up (not just 44px), in
   * a tighter three-line layout. For the allocation column, whose labels
   * extend toward the group bars — an inline sublabel there can run under
   * a bar painted later, while the other columns' labels face open space.
   */
  wrapSublabel?: boolean;
};

// Two lines when the node is tall enough, one compact line otherwise —
// short nodes stacked close together collide with two-line labels.
const NodeLabel = ({
  laid,
  x,
  anchor,
  formatValue,
  share,
  wrapSublabel = false,
}: NodeLabelProps) => {
  const {node, y, h} = laid;
  const mid = y + h / 2;
  const compressed = laid.drawnValue < node.value;
  const sublabel = compressed ? 'scale break' : node.sublabel;
  const value =
    formatValue(node.value) + (share != null ? ` · ${share}` : '');
  // The share lengthened the one-line form; compact labels shorten the
  // name to keep the whole line inside the label budget.
  const label = truncate(node.label, h < 30 && share != null ? 18 : 24);
  // A tall node with a sublabel puts it on its own line, so the value
  // line stays short and never runs under a neighboring column's bar;
  // wrapSublabel extends this down to 30px bars with tighter spacing.
  if (sublabel && (h >= 44 || (wrapSublabel && h >= 30))) {
    const roomy = h >= 44;
    return (
      <>
        <text
          x={x}
          y={mid - (roomy ? 8 : 12)}
          textAnchor={anchor}
          className="fill-foreground text-[13px] font-semibold"
        >
          {label}
        </text>
        <text
          x={x}
          y={mid + (roomy ? 7 : 1.5)}
          textAnchor={anchor}
          className="fill-muted-foreground text-[11.5px]"
        >
          {value}
        </text>
        <text
          x={x}
          y={mid + (roomy ? 21 : 15)}
          textAnchor={anchor}
          className="fill-muted-foreground text-[11.5px]"
        >
          {sublabel}
        </text>
      </>
    );
  }
  if (h >= 30) {
    return (
      <>
        <text
          x={x}
          y={mid - 1}
          textAnchor={anchor}
          className="fill-foreground text-[13px] font-semibold"
        >
          {label}
        </text>
        <text
          x={x}
          y={mid + 14}
          textAnchor={anchor}
          className="fill-muted-foreground text-[11.5px]"
        >
          {value}
          {sublabel ? ` · ${sublabel}` : ''}
        </text>
      </>
    );
  }
  return (
    <text
      x={x}
      y={mid + 4.5}
      textAnchor={anchor}
      className="fill-foreground text-[12.5px] font-semibold"
    >
      {label}{' '}
      <tspan className="fill-muted-foreground text-[11.5px] font-normal">
        · {value}
        {sublabel ? ` · ${sublabel}` : ''}
      </tspan>
    </text>
  );
};

type DrillableProps = {
  laid: Laid;
  onSelectCategory?: (key: string) => void;
  formatValue: (cents: number) => string;
  /** The node's share of its column, included in the accessible name. */
  share?: string | null;
  children: React.ReactNode;
};

// Shared click/keyboard/aria wrapper for any node that carries a key.
const Drillable = ({
  laid,
  onSelectCategory,
  formatValue,
  share,
  children,
}: DrillableProps) => {
  const key = laid.node.key;
  const drillable = onSelectCategory != null && key != null;
  return (
    <g
      onClick={drillable ? () => onSelectCategory(key) : undefined}
      onKeyDown={
        drillable
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelectCategory(key);
              }
            }
          : undefined
      }
      role={drillable ? 'button' : undefined}
      tabIndex={drillable ? 0 : undefined}
      aria-label={
        drillable
          ? `${laid.node.label}: ${formatValue(laid.node.value)}${
              share != null ? ` (${share})` : ''
            } — break down`
          : undefined
      }
      className={drillable ? 'cursor-pointer' : undefined}
    >
      {children}
    </g>
  );
};

/**
 * The tiered sankey: income sources merge into a single income spine, the
 * spine splits into the taxes / spending / saving allocation boxes, and
 * Spending fans out into categories. All geometry derives from the node
 * columns — sources dock onto the spine in order, allocations take
 * contiguous offtakes from it in order, so no attribution between a
 * particular source and a particular allocation is drawn or implied.
 */
export const TieredSankey = (props: Props) => {
  const {model, onSelectCategory, formatValue} = props;
  const {sources, allocs, groupCol, cats} = model;

  const total = sources.reduce((sum, node) => sum + drawnValueOf(node), 0);
  if (total <= 0) {
    return (
      <div className="text-muted-foreground py-12 text-center text-sm">
        Nothing in this window.
      </div>
    );
  }
  const scale = CHART_H / total;
  // Share bases use TRUE values (never drawn ones) so a scale break can't
  // distort percentages: sources and allocations against the spine,
  // groups and member categories against Spending.
  const spineTrue = sources.reduce((sum, node) => sum + node.value, 0);
  const spendingTrue = groupCol.reduce((sum, node) => sum + node.value, 0);
  const left = layout(sources, scale);
  const mid = layout(allocs, scale);
  const groupsLaid = layout(groupCol, scale);
  const right = layout(cats, scale);

  // Spending's right edge apportions among the group column by drawn
  // value, so the fan mouths stay glued to the box.
  const spendingLaid = mid.find((laid) => laid.node.kind === 'spending-tier');
  const groupsDrawnTotal = groupsLaid.reduce(
    (sum, laid) => sum + laid.drawnValue,
    0,
  );
  // With no groups configured the cats column is empty and the group
  // column is the category fold — a four-column diagram using the legacy
  // geometry, so ungrouped users see the exact pre-tier layout with no
  // extra width.
  const hasCats = right.length > 0;
  const viewW = hasCats ? VIEW_W : 1000;
  const xMid = hasCats ? X_MID : 555;
  const xGroups = hasCats ? X_GROUP : 795;

  const bottomOf = (side: Laid[]): number => {
    const last = side.at(-1);
    return last == null ? TOP : last.y + last.h;
  };
  const height =
    Math.max(
      bottomOf(left),
      bottomOf(mid),
      bottomOf(groupsLaid),
      bottomOf(right),
      TOP + CHART_H,
    ) + 16;

  let groupOffset = 0;

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${viewW} ${height}`}
        className="w-full min-w-[760px]"
        aria-label="Cash flow diagram"
      >
        {/* Source → spine ribbons */}
        {left.map((laid) => (
          <path
            key={`sr-${laid.node.kind}-${laid.node.key ?? laid.node.label}`}
            d={ribbonPath(
              X_LEFT + NODE_W,
              laid.y,
              laid.h,
              X_SPINE,
              laid.spineY,
              laid.drawnValue * scale,
            )}
            fill={laid.color}
            opacity={
              laid.node.kind === 'income'
                ? 0.35
                : laid.node.kind === 'unreviewed'
                  ? 0.2
                  : 0.45
            }
          >
            <title>{`${laid.node.label} — ${formatValue(laid.node.value)}`}</title>
          </path>
        ))}

        {/* Spine → allocation ribbons */}
        {mid.map((laid) => (
          <path
            key={`ar-${laid.node.kind}`}
            d={ribbonPath(
              X_SPINE + NODE_W,
              laid.spineY,
              laid.drawnValue * scale,
              xMid,
              laid.y,
              laid.h,
            )}
            fill={laid.color}
            opacity={laid.node.kind === 'unreviewed' ? 0.2 : 0.35}
          >
            <title>{`${laid.node.label} — ${formatValue(laid.node.value)}`}</title>
          </path>
        ))}

        {/* Spending → group-column ribbons */}
        {spendingLaid != null &&
          groupsDrawnTotal > 0 &&
          groupsLaid.map((laid) => {
            const share = laid.drawnValue / groupsDrawnTotal;
            const mouthY =
              spendingLaid.y +
              (groupOffset / groupsDrawnTotal) * spendingLaid.h;
            const mouthH = share * spendingLaid.h;
            groupOffset += laid.drawnValue;
            return (
              <path
                key={`gr-${laid.node.groupId ?? laid.node.key ?? laid.node.label}`}
                d={ribbonPath(
                  xMid + NODE_W,
                  mouthY,
                  mouthH,
                  xGroups,
                  laid.y,
                  laid.h,
                )}
                fill={laid.color}
                opacity={0.5}
              >
                <title>{`${laid.node.label} — ${formatValue(laid.node.value)}`}</title>
              </path>
            );
          })}

        {/* Group → member-category ribbons: each aggregate fans into
            exactly its children, apportioned along the group bar. */}
        {groupsLaid
          .filter(
            (parent) =>
              parent.node.kind === 'spend-group' && parent.node.groupId != null,
          )
          .map((parent) => {
            const children = right.filter(
              (child) => child.node.groupId === parent.node.groupId,
            );
            const childTotal = children.reduce(
              (sum, child) => sum + child.drawnValue,
              0,
            );
            if (childTotal <= 0) {
              return null;
            }
            let offset = 0;
            return children.map((child) => {
              const mouthY = parent.y + (offset / childTotal) * parent.h;
              const mouthH = (child.drawnValue / childTotal) * parent.h;
              offset += child.drawnValue;
              return (
                <path
                  key={`mr-${String(parent.node.groupId)}-${child.node.key ?? child.node.label}`}
                  d={ribbonPath(
                    X_GROUP + NODE_W,
                    mouthY,
                    mouthH,
                    X_RIGHT,
                    child.y,
                    child.h,
                  )}
                  fill={child.color}
                  opacity={0.5}
                >
                  <title>{`${child.node.label} — ${formatValue(child.node.value)}`}</title>
                </path>
              );
            });
          })}

        {/* The spine: every income, one bar. Its height includes the
            From-savings band when the window overspent and the Unreviewed
            band when pending inflows net positive, so the header must not
            call either of those income — the caption reports them
            separately. */}
        <text
          x={X_SPINE + NODE_W / 2}
          y={TOP - 8}
          textAnchor="middle"
          className="fill-muted-foreground text-[11.5px]"
        >
          {spineHeading(model)}
        </text>
        <rect
          x={X_SPINE}
          y={TOP}
          width={NODE_W}
          height={total * scale}
          rx={2}
          className="fill-border"
        />

        {/* Sources; drillable when the node carries a key. */}
        {left.map((laid) => (
          <Drillable
            key={`ln-${laid.node.kind}-${laid.node.key ?? laid.node.label}`}
            laid={laid}
            onSelectCategory={onSelectCategory}
            formatValue={formatValue}
            share={formatShare(laid.node.value, spineTrue)}
          >
            <rect
              x={X_LEFT}
              y={laid.y}
              width={NODE_W}
              height={laid.h}
              rx={2}
              fill={laid.color}
              {...nodeRectStyle(laid)}
            />
            <NodeLabel
              laid={laid}
              x={X_LEFT - 10}
              anchor="end"
              formatValue={formatValue}
              share={formatShare(laid.node.value, spineTrue)}
            />
            {laid.drawnValue < laid.node.value && (
              <BreakGlyph
                x0={X_LEFT - 5}
                x1={X_LEFT + NODE_W + 42}
                y={laid.y + laid.h * 0.62}
              />
            )}
          </Drillable>
        ))}

        {/* Allocation bars, styled like every other column; labels sit to
            the right — clean space for Taxes and Saving, and legible over
            the translucent fan for Spending. Taxes drills when bound. */}
        {mid.map((laid) => (
          <Drillable
            key={`mn-${laid.node.kind}`}
            laid={laid}
            onSelectCategory={onSelectCategory}
            formatValue={formatValue}
            share={formatShare(laid.node.value, spineTrue)}
          >
            <rect
              x={xMid}
              y={laid.y}
              width={NODE_W}
              height={laid.h}
              rx={2}
              fill={laid.color}
              {...nodeRectStyle(laid)}
            />
            <NodeLabel
              laid={laid}
              x={xMid + 18}
              anchor="start"
              formatValue={formatValue}
              share={formatShare(laid.node.value, spineTrue)}
              wrapSublabel
            />
            {laid.drawnValue < laid.node.value && (
              <BreakGlyph
                x0={xMid - 42}
                x1={xMid + NODE_W + 5}
                y={laid.y + laid.h * 0.62}
              />
            )}
          </Drillable>
        ))}

        {/* Group column: aggregates (not drillable — their children are
            visible) and standalone categories (drillable). */}
        {groupsLaid.map((laid) => (
          <Drillable
            key={`gn-${laid.node.groupId ?? laid.node.key ?? laid.node.label}`}
            laid={laid}
            onSelectCategory={onSelectCategory}
            formatValue={formatValue}
            share={formatShare(laid.node.value, spendingTrue)}
          >
            <rect
              x={xGroups}
              y={laid.y}
              width={NODE_W}
              height={laid.h}
              rx={2}
              fill={laid.color}
            />
            <NodeLabel
              laid={laid}
              x={xGroups + 14}
              anchor="start"
              formatValue={formatValue}
              share={formatShare(laid.node.value, spendingTrue)}
            />
          </Drillable>
        ))}

        {/* Member categories; drillable when the node carries a key */}
        {right.map((laid) => (
          <Drillable
            key={`rn-${String(laid.node.groupId)}-${laid.node.key ?? laid.node.label}`}
            laid={laid}
            onSelectCategory={onSelectCategory}
            formatValue={formatValue}
            share={formatShare(laid.node.value, spendingTrue)}
          >
            <rect
              x={X_RIGHT}
              y={laid.y}
              width={NODE_W}
              height={laid.h}
              rx={2}
              fill={laid.color}
            />
            <NodeLabel
              laid={laid}
              x={X_RIGHT + 18}
              anchor="start"
              formatValue={formatValue}
              share={formatShare(laid.node.value, spendingTrue)}
            />
          </Drillable>
        ))}

      </svg>
    </div>
  );
};
